import { IntRange, range } from "@/lib/diff/misc.ts"
import { filterNotNull, firstNotNullOf, isNull, isNullish, lastOrNull } from "@/lib/std.ts"

import type { CodeScopeRange, InlineDiff, InlineDiffLine } from "@/lib/client/generated/TypeDefinitions.ts"


const MIN_COLLAPSED_LINES = 2
const CONTEXT_LINES = 2

export function getInlineDiffCollapsedLinesRange(
  diff: InlineDiff,
  expanded: IntRange[] = [],
  params: Partial<InlineDiffCollapsedParams> = {}
) {
  const numberOfContextLines = params.numberOfContextLines || CONTEXT_LINES
  const minimumNumberOfCollapsedLines = params.minimumNumberOfCollapsedLines || MIN_COLLAPSED_LINES
  const nonCollapsibleDiffLinesRanges = getNonCollapsibleDiffRanges(diff, numberOfContextLines)

  return getCollapsedLinesRanges(
    [range(0, diff.lines.length - 1)],
    nonCollapsibleDiffLinesRanges.concat(expanded),
    minimumNumberOfCollapsedLines,
  )
}


function getNonCollapsibleDiffRanges(diff: InlineDiff, numberOfContextLines: number): IntRange[] {
  const scopes = filterNotNull(diff.scopes || [])

  let expandedLines = getChangedLineRanges(diff)
    .map(range => expandContextToScopes(diff.lines, range, numberOfContextLines, scopes))

  if (expandedLines.length === 0) {
    // Show the first few lines if there is no diff
    expandedLines = [new IntRange(0, Math.min(numberOfContextLines, diff.lines.length))]
  }

  return expandedLines
}


function getChangedLineRanges(diff: InlineDiff): IntRange[] {
  const changedRanges: IntRange[] = []
  diff.lines.forEach((line, index) => {
    if (isNullish(line.type)) {
      return
    }

    const prevRange = changedRanges[changedRanges.length - 1] || null
    if (prevRange === null || prevRange.endInclusive < index - 1) {
      return changedRanges.push(new IntRange(index, index))
    }

    changedRanges[changedRanges.length - 1] = new IntRange(prevRange.start, index)
  })

  return changedRanges
}


function expandContextToScopes(lines: InlineDiffLine[], fragment: IntRange, maxContent: number, scopes: CodeScopeRange[] | null): IntRange {
  const fStart = fragment.start
  const topContext = (() => {
    if (fStart <= maxContent) {
      return fStart
    }

    // Find the nearest scope border
    let scopeBorder = range(0, maxContent).firstOrNull(ctx => {
      const line = fStart - ctx - 1
      if (!scopes) {
        return false
      }

      return scopes.some(it => it.startLine + it.linesCount - 1 === line)
    })

    if (isNull(scopeBorder)) {
      scopeBorder = maxContent
    }

    for (let i = scopeBorder; i >= 1; i--) {
      const isNotBlankLine = lines[fStart - i]?.text.length > 0
      if (isNotBlankLine) {
        return i
      }
    }

    return 0
  })()

  const fEndInclusive = fragment.endInclusive
  const bottomContext = (() => {
    if (fEndInclusive >= lines.length - 1 - maxContent) {
      return lines.length - 1 - fEndInclusive
    }

    const bottomLineStartsScope = lastOrNull(scopes || [], it =>
      firstNotNullOf(it.prefixLine, it.startLine) === fEndInclusive)

    // find the nearest scope prefix
    let scopeBorder = range(0, maxContent).firstOrNull(ctx => {
      const line = fEndInclusive + ctx + 1
      if (bottomLineStartsScope) {
        const lineRange = range(bottomLineStartsScope.startLine, bottomLineStartsScope.startLine + bottomLineStartsScope.linesCount)
        if (lineRange.contains(line)) {
          // do not count nested range, see FileDiffBuilderTest::test_context_lines_nested_scope
          return false
        }
      }

      if (!scopes) {
        return false
      }

      return scopes.some(it => firstNotNullOf(it.prefixLine, it.startLine) === line)
    })

    if (isNull(scopeBorder)) {
      scopeBorder = maxContent
    }

    for (let i = scopeBorder; i >= 1; i--) {
      const isNotBlankLine = lines[fEndInclusive + i]?.text.length > 0
      if (isNotBlankLine) {
        return i
      }
    }

    return 0
  })()

  return range(fStart - topContext, fEndInclusive + bottomContext)
}


function getCollapsedLinesRanges(
  collapsibleLinesRanges: IntRange[],
  nonCollapsibleRightLinesRanges: IntRange[] | null,
  minimumNumberOfCollapsedLines: number
) {
  nonCollapsibleRightLinesRanges = nonCollapsibleRightLinesRanges || []
  let ranges = collapsibleLinesRanges
  if (nonCollapsibleRightLinesRanges) {
    ranges = collapsibleLinesRanges.flatMap(it => {
      let newRanges = [it]
      nonCollapsibleRightLinesRanges.forEach(it => {
        const newCollection: IntRange[] = []
        newRanges.forEach(range => newCollection.push(...range.minus(it)))
        newRanges = newCollection
      })

      return newRanges
    })

    return ranges
  }

  return ranges.filter(range =>
    range.size >= minimumNumberOfCollapsedLines)
}


type InlineDiffCollapsedParams = {
  minimumNumberOfCollapsedLines: number,
  numberOfContextLines: number,
  expandStep: number,
}